import { useMemo } from 'react'
import classNames from 'classnames'
import { FormLabel } from './FormLabel'
import { FormikErrors } from 'formik'

interface FormTextProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  isRequired?: boolean
  isRequiredLabel?: boolean
  name?: string
  error?: string | string[] | FormikErrors<any> | FormikErrors<any>[]
  className?: string
  clickeable?: boolean
  onClick?: () => void
  ref?: React.Ref<HTMLInputElement>
}

interface FormSimpleRadioGroupOption<T> {
  name: string
  value: T
  disabled?: boolean
  tooltip?: string
}
interface FormSimpleRadioGroupProps<T> extends FormTextProps {
  options: FormSimpleRadioGroupOption<T>[]
  selected?: T
  disabled?: boolean
  onSelectOption: (opt: T) => void
  description?: string
  horizontal?: boolean
  optionsClassName?: string
  wrapperClassName?: string
}
export const FormSimpleRadioGroup = <T extends any>({
  label,
  name,
  selected,
  disabled,
  onSelectOption,
  options,
  className,
  isRequiredLabel,
  description = '',
  horizontal = false,
  optionsClassName,
  wrapperClassName,
  ...props
}: FormSimpleRadioGroupProps<T>) => {
  const Options = useMemo(() => {
    return options.map((option, index) => (
      <div
        key={index}
        className="flex items-center"
        title={option.tooltip}
        data-te-toggle="tooltip"
      >
        <input
          id={`option-${name}-${option.name}-${index}`}
          name={`simple-radio-group-${name}-${index}`}
          type="radio"
          disabled={disabled || option.disabled}
          checked={option.value === selected}
          onChange={() =>
            !disabled && !option.disabled && onSelectOption(option.value)
          }
          className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
        />
        <label
          className={classNames(
            'ml-3 block text-sm font-medium leading-6 truncate',
            (disabled || option.disabled) && 'text-gray-400',
            optionsClassName
          )}
        >
          {option.name}
        </label>
      </div>
    ))
  }, [options, name, disabled, selected, optionsClassName, onSelectOption])

  return (
    <div className={className} {...props}>
      {!!label && (
        <FormLabel
          label={label}
          htmlFor={props.id}
          isRequired={isRequiredLabel}
        />
      )}
      <p className="text-m text-gray-500">{description}</p>
      <fieldset>
        <div
          className={classNames(
            'flex gap-4 flex-wrap',
            horizontal ? 'flex-row items-center' : 'flex-col',
            wrapperClassName
          )}
        >
          {Options}
        </div>
      </fieldset>
    </div>
  )
}
